import {Button} from '@wandb/components';
import React, {memo, useState} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Modal} from 'semantic-ui-react';

import {GetTaskComponent, TaskState} from '../generated/graphql';
import {ProjectPicker} from './ProjectPicker/ProjectPicker';
import {useRunSelectorAnalyticsLocation} from './RunSelector/RunSelectorAnalyticsLocationContext';

interface RunMoverProps {
  entityName: string;
  requireSameEntity?: boolean;
  requireSameEntityReason?: string;
  projectInternalId: string;
  projectName: string;
  selectedCount: number;
  refetch(): void;
  onMove(
    destinationEntityName: string,
    destinationProjectName: string
  ): Promise<string>;
}

const RunMoverComp: React.FC<RunMoverProps> = ({
  entityName,
  requireSameEntity,
  requireSameEntityReason,
  projectInternalId,
  projectName,
  selectedCount,
  refetch,
  onMove,
}) => {
  const analyticsLocation = useRunSelectorAnalyticsLocation();
  const [popupOpen, setPopupOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [destinationEntityName, setDestinationEntityName] = useState<
    string | undefined
  >();
  const [destinationProjectName, setDestinationProjectName] = useState<
    string | undefined
  >();
  const [inProgressTaskID, setInProgressTaskID] = useState<
    string | undefined
  >();

  let button: React.ReactNode;
  let buttonClass: string;

  if (inProgressTaskID) {
    button = (
      <RunMoverProgress
        inProgressTaskID={inProgressTaskID!}
        onFinished={() => {
          setInProgressTaskID(undefined);
          refetch();
        }}
        onError={() => {
          setInProgressTaskID(undefined);
          setErrorOpen(true);
        }}
      />
    );
  } else {
    button = (
      <Button
        isActive={popupOpen}
        startIcon="folder-project-move"
        variant="secondary"
        onPress={() => {
          window.analytics?.track('Move button clicked', {
            location: analyticsLocation,
          });
        }}>
        Move to project
      </Button>
    );
  }

  if (selectedCount === 0) {
    return null;
  }

  return (
    <React.Fragment>
      <ProjectPicker
        entityName={entityName}
        requireSameEntity={requireSameEntity}
        requireSameEntityReason={requireSameEntityReason}
        projectId={projectInternalId}
        projectName={projectName}
        onOpen={() => setPopupOpen(true)}
        onClose={() => setPopupOpen(false)}
        setProject={(entity, proj) => {
          setDestinationEntityName(entity);
          setDestinationProjectName(proj);
          setModalOpen(true);
        }}
        triggerOverride={button}
      />
      <Modal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setPopupOpen(false);
        }}>
        <Modal.Content>
          <p>
            {`Are you sure you want to move ${selectedCount} ${
              selectedCount === 1 ? 'run' : 'runs'
            } to project `}
            <b>{`${destinationEntityName!}/${destinationProjectName!}`}</b>?
          </p>
          <p className="disclaimer">
            Note: Any runs that are still running will not be moved. Artifacts
            associated with the runs will not be moved.
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button
            mr={4}
            onPress={() => {
              onMove(destinationEntityName!, destinationProjectName!).then(
                taskID => {
                  setInProgressTaskID(taskID);
                  setModalOpen(false);
                  setPopupOpen(false);
                }
              );
            }}>
            Move runs
          </Button>
          <Button
            variant="ghost"
            data-test="run-move-popup"
            onPress={() => {
              setModalOpen(false);
              setPopupOpen(false);
            }}>
            Nevermind
          </Button>
        </Modal.Actions>
      </Modal>
      <Modal open={errorOpen}>
        <Modal.Content>
          <p>
            Some of your runs didn't make the move. Please try moving them
            again. If this keeps happening, you can message us with the chat in
            the lower right corner of the page.
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button onPress={() => setErrorOpen(false)}>Ok</Button>
        </Modal.Actions>
      </Modal>
    </React.Fragment>
  );
};

export const RunMover = memo(RunMoverComp);

interface RunMoverProgressProps {
  inProgressTaskID: string;
  onFinished: () => void;
  onError: () => void;
}

const RunMoverProgress: React.FC<RunMoverProgressProps> = React.memo(
  ({inProgressTaskID, onFinished, onError}) => {
    const [progressText, setProgressText] = useState('Moving...');

    return (
      <GetTaskComponent
        pollInterval={500}
        fetchPolicy="no-cache"
        notifyOnNetworkStatusChange
        onCompleted={({task}) => {
          const {state, progress} = task!;

          setProgressText(`Moving... (${progress}%)`);

          if (state === TaskState.Finished) {
            onFinished();
          } else if (state === TaskState.Failed) {
            onError();
          }
        }}
        onError={() => onError()}
        variables={{
          id: inProgressTaskID,
        }}>
        {() => {
          return (
            <Button isDisabled startIcon="folder-project-move" variant="ghost">
              {progressText}
            </Button>
          );
        }}
      </GetTaskComponent>
    );
  }
);
